// sendChatStart.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import getMeData from '../selector/getMeData.js';
import { push, replace } from '../action/navigationAction.js';
import postMessageToApp from '../action/postMessageToApp.js';
import getAppUniversalLink from '../resource/getAppUniversalLink.js';
import getSchatUniversalLink from '../resource/getSchatUniversalLink.js';
import getRouterData from '../selector/getRouterData.js';

import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import login from '../action/login.js';
import getUserData from '../selector/getUserData.js';

/**
 * Send chat start
 * @kind action
 * @param {string} { userId } - user id.
 * @param {('browserHistoryPush' | 'schatInAppWebView' | 'swagrInAppWebView')} { [redirectMethod] } handle route redirect method
 * @return {Promise} Action promise.
 */

const sendChatStart =
  ({ userId, redirectMethod = 'browserHistoryPush', isInChatRoom = false }) =>
  async (dispatch, getState) => {
    let token = getMeData(getState(), 'token');
    const replyPrice = getUserData(getState(), userId, 'replyPrice');
    if (!token) {
      if (isInChatRoom && replyPrice === 0) {
        // If this is a free chat flow, we should open the guest mode / login modal here
        // so the user can continue entering the chat as a guest-authenticated user.
        // For paid chat, payment handling should be deferred to `checkChatMessageDraft`.
        // That flow will determine whether to open `openQuickpayModal` or trigger `addvalue`
        // based on the user's current purchase / balance state.
        await dispatch(login());
        token = getMeData(getState(), 'token');
        if (!token) return dispatch(push(`/chat/user/${userId}`));
      } else {
        return dispatch(push(`/chat/user/${userId}`));
      }
    }

    const fetchOptions = {
      method: 'HEAD',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    const selectPath = ['chat', userId];
    const url = getResourceUrl({ endpoint: `/user/${userId}/chat` });

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const location = response.headers.get('Location');
      const chatroomId = location.split('/').slice(-1)[0];

      dispatch({ type: SET_NETWORKING_SUCCESS, payload: { selectPath } });
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['userChatroomId'],
          data: { [userId]: chatroomId },
        },
      });

      const direction = {
        view: 'chatroom',
        'chatroom-id': chatroomId,
      };

      const from = getRouterData(getState(), ['location']);
      switch (redirectMethod) {
        case 'schatInAppWebView':
          return dispatch(
            postMessageToApp({
              message: getSchatUniversalLink({
                search: direction,
              }),
            })
          );
        case 'swagrInAppWebView':
          return dispatch(
            postMessageToApp({
              message: 'actionUrl',
              messageArgs: [
                getAppUniversalLink({
                  search: direction,
                }),
              ],
            })
          );
        case 'browserHistoryReplace':
        case 'browserHistoryPush':
        default: {
          const path = `/chat/${chatroomId}`;
          if ('browserHistoryReplace' === redirectMethod) {
            return dispatch(
              replace(path, {
                from,
              })
            );
          }
          return dispatch(push(path, { from }));
        }
      }
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default sendChatStart;
